import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { Lightbulb, Layout, ClipboardList, Film, LogOut } from 'lucide-react';
import { useAuth } from './lib/hooks/useAuth';
import { CriteriaForm } from './components/CriteriaForm';
import { Button } from './components/ui/button';
import { Register } from './components/auth/Register';
import { RegisterForm } from './components/auth/RegisterForm';
import { Login } from './components/auth/Login';
import { FilmmakerDashboard } from './components/FilmmakerDashboard';
import { FilmmakerQuestionnaire } from './components/FilmmakerQuestionnaire';

function App() {
  const { user, profile, loading, signOut } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#212323] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#F8B500]"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-[#212323] text-white">
        {user ? (
          <div className="flex">
            {/* Sidebar */}
            <div className="fixed left-0 top-0 h-full w-64 bg-gray-900 border-r border-gray-800">
              <div className="p-6">
                <div className="flex items-center space-x-3">
                  <Lightbulb className="h-8 w-8 text-[#F8B500]" />
                  <h1 className="text-xl font-bold">
                    PITCH<span className="text-[#F8B500]">lift</span>
                  </h1>
                </div>
              </div>
              
              <nav className="mt-6">
                <div className="px-4 space-y-2">
                  <Link
                    to="/dashboard"
                    className="flex items-center space-x-3 px-4 py-3 text-gray-300 hover:bg-gray-800 rounded-lg"
                  >
                    <Layout className="h-5 w-5" />
                    <span>Dashboard</span>
                  </Link>
                  <Link
                    to="/projects"
                    className="flex items-center space-x-3 px-4 py-3 text-gray-300 hover:bg-gray-800 rounded-lg"
                  >
                    <Film className="h-5 w-5" />
                    <span>Projects</span>
                  </Link>
                  {profile?.user_type === 'studio' && (
                    <Link
                      to="/criteria"
                      className="flex items-center space-x-3 px-4 py-3 text-gray-300 hover:bg-gray-800 rounded-lg"
                    >
                      <ClipboardList className="h-5 w-5" />
                      <span>Criteria</span>
                    </Link>
                  )}
                </div>
              </nav>

              <div className="absolute bottom-0 w-full p-4">
                <button
                  onClick={() => signOut()}
                  className="flex items-center space-x-3 px-4 py-3 w-full text-gray-300 hover:bg-gray-800 rounded-lg"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>

            {/* Main Content */}
            <div className="ml-64 flex-1">
              <Routes>
                <Route 
                  path="/dashboard" 
                  element={
                    profile?.user_type === 'filmmaker' ? (
                      <FilmmakerDashboard />
                    ) : (
                      <Dashboard profile={profile} />
                    )
                  } 
                />
                <Route path="/projects" element={<div>Projects Page</div>} />
                <Route path="/criteria" element={<CriteriaForm />} />
                <Route path="/filmmaker-application" element={<FilmmakerQuestionnaire />} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </div>
          </div>
        ) : (
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register/studio" element={<RegisterForm userType="studio" />} />
            <Route path="/register/filmmaker" element={<RegisterForm userType="filmmaker" />} />
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </div>
    </Router>
  );
}

function HomePage() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center">
      <div className="flex items-center mb-8">
        <Lightbulb className="h-16 w-16 text-[#F8B500]" />
        <h1 className="text-4xl font-bold text-white ml-4">
          PITCH<span className="text-[#F8B500]">lift</span>
        </h1>
      </div>
      
      <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
        Welcome to PITCHlift™
      </h2>
      
      <p className="text-xl md:text-2xl text-gray-300 mb-4">
        Connecting purpose-driven studios
      </p>
      <p className="text-xl md:text-2xl text-gray-300 mb-12">
        with visionary filmmakers.
      </p>

      <div className="space-y-4 w-full max-w-xs">
        <Link to="/register">
          <Button className="w-full py-6 text-lg" size="lg">
            SIGN UP
          </Button>
        </Link>
        <Link to="/login">
          <Button variant="outline" className="w-full py-6 text-lg" size="lg">
            SIGN IN
          </Button>
        </Link>
      </div>
    </div>
  );
}

function Dashboard({ profile }: { profile: any }) {
  if (!profile) {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-4">Welcome!</h2>
        <p className="text-gray-400">Loading your profile information...</p>
      </div>
    );
  }

  const userName = profile?.first_name ? `Welcome back, ${profile.first_name}!` : 'Welcome back!';
  
  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold">{userName}</h1>
        <p className="text-gray-400">{profile?.email}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Active Projects</h3>
          <p className="text-3xl font-bold text-[#F8B500]">3</p>
        </div>
        
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Pending Reviews</h3>
          <p className="text-3xl font-bold text-[#F8B500]">2</p>
        </div>
        
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Total Submissions</h3>
          <p className="text-3xl font-bold text-[#F8B500]">12</p>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
        <div className="bg-gray-800 rounded-lg">
          <div className="p-4 border-b border-gray-700">
            <p className="text-sm text-gray-300">New submission received from Sarah Chen</p>
            <p className="text-xs text-gray-500 mt-1">2 hours ago</p>
          </div>
          <div className="p-4 border-b border-gray-700">
            <p className="text-sm text-gray-300">Project "The Last Journey" updated</p>
            <p className="text-xs text-gray-500 mt-1">5 hours ago</p>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-300">Submission criteria updated</p>
            <p className="text-xs text-gray-500 mt-1">1 day ago</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;